import { useEffect, useRef, useCallback } from "react";

// Public site key (safe to expose, baked into the bundle at build time).
export const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

const SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const SCRIPT_ID = "cf-turnstile-script";

function loadScript() {
  return new Promise((resolve, reject) => {
    if (window.turnstile) return resolve();
    let el = document.getElementById(SCRIPT_ID);
    if (el) {
      el.addEventListener("load", () => resolve());
      el.addEventListener("error", reject);
      return;
    }
    el = document.createElement("script");
    el.id = SCRIPT_ID;
    el.src = SCRIPT_SRC;
    el.async = true;
    el.defer = true;
    el.addEventListener("load", () => resolve());
    el.addEventListener("error", reject);
    document.head.appendChild(el);
  });
}

/**
 * Renders a Cloudflare Turnstile widget. Calls onVerify(token) when solved and
 * onVerify("") when the token expires. Renders nothing if no site key is set
 * (e.g. local dev), so forms still work during development.
 */
export const Turnstile = ({ onVerify, className }) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  const handleVerify = useCallback((token) => onVerify?.(token), [onVerify]);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return undefined;
    let cancelled = false;

    loadScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: handleVerify,
          "expired-callback": () => handleVerify(""),
          "error-callback": () => handleVerify(""),
          theme: "light",
        });
      })
      .catch(() => {
        /* network/script error — leave token empty, server will reject */
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          /* noop */
        }
      }
    };
  }, [handleVerify]);

  if (!TURNSTILE_SITE_KEY) return null;
  return <div ref={containerRef} className={className} data-testid="turnstile-widget" />;
};
